import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Box, Typography, Divider } from '@mui/material';
import CloseIcon from '../../../../assets/closeIcon';
import { CustomReactSelect, CustomButton, CustomSwitch } from '../../../shared';
import { useDispatch, useSelector } from 'react-redux';
import { actions as ChannelActions } from '../../../../redux/reducers/adminSettingsReducer';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { validateForScriptTag } from '../../../../utility';
import {
    editChannelAction,
    getChannelActions,
    getPartnersChannels,
    postChannelAction,
} from '../../../../redux/thunks/adminSettingsThunk';

const initialValue = {
    name: '',
    channel: '',
    engagement: false,
};

const dataSourcesSchema = yup
    .object({
        name: yup
            .string()
            .required('The action name field is required.')
            .test(
                'no-script-tag',
                'Script tags are not allowed.',
                validateForScriptTag
            ),
        channel: yup.string().required('The channel field is required.'),
        engagement: yup.boolean(),
    })
    .required();

function EditCreateChannelActions(props) {
    const { handleClose } = props;
    const dispatch = useDispatch();
    const { company } = useSelector((state) => state.company);
    const { channelActions, vendorSetup } = useSelector((state) => state.adminSettings);
    const { type, showModal, channelActionDataById } = channelActions;
    const { partnerChannels } = vendorSetup;
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [isEngaged, setIsEngaged] = useState(false);
    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(dataSourcesSchema),
        defaultValues: initialValue,
    });

    const handleChannelChange = (selectedOption) => {
        setSelectedChannel(selectedOption);
        setValue('channel', selectedOption.label);
    };

    const partnerChannelsOptions = useMemo(
        () =>
            partnerChannels.map((item) => ({
                id: item.campaign_type.id,
                label: item.campaign_type.name,
            })),
        [partnerChannels]
    );

    const uniquePartnerChannelsOptions = useMemo(
        () =>
            partnerChannelsOptions.filter(
                (obj, index, self) =>
                    index === self.findIndex((o) => o.id === obj.id)
            ),
        [partnerChannelsOptions]
    );

    const onSubmit = (data) => {
        if (type === 'Create') {
            const finalData = {
                company_id: company.id,
                campaign_type_id: selectedChannel.id,
                action_name: getValues('name').toLocaleLowerCase(),
                engagement: getValues('engagement'),
            };
            dispatch(postChannelAction({ data: finalData })).then((result) => {
                if (!result.error) {
                    dispatch(getChannelActions({ companyId: company.id }));
                    dispatch(ChannelActions.handleChannelActionModal(''));
                }
            });
        }
        if (type === 'Edit' && Object.keys(channelActionDataById).length) {
          const finalData = {
              company_id: company.id,
              campaign_type_id: selectedChannel.id,
              action_name: getValues('name').toLocaleLowerCase(),
              engagement: getValues('engagement'),
          };
          dispatch(editChannelAction({ data: finalData, id: channelActionDataById.id })).then(
              (result) => {
                  if (!result.error) {
                      dispatch(getChannelActions({ companyId: company.id }));
                      dispatch(ChannelActions.handleChannelActionModal(''));
                  }
              }
          );
      }
    };
    
    const handleSave = () => {
      handleSubmit(onSubmit);
  };

    const handleIsEngagementChange = () => {
        const engagement = getValues('engagement');
        setValue('engagement', !engagement);
        setIsEngaged(!isEngaged);
    };

    useEffect(() => {
        if (company.id) {
            dispatch(getPartnersChannels({ companyId: company.id }));
        }
    }, [company.id]);

    useEffect(() => {
        if (Object.keys(channelActionDataById).length) {
            setValue('name', channelActionDataById.campaign_type_action.action);
            setValue('channel',channelActionDataById.campaign_type.name);
            setValue('engagement', Boolean(channelActionDataById.engagement));
            setSelectedChannel({
                id: channelActionDataById.campaign_type.id,
                label: channelActionDataById.campaign_type.name,
            });
            setIsEngaged(Boolean(channelActionDataById.engagement))
        }
    }, [channelActionDataById]);

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="data_source_create_new"
        >
            <Grid container className="modal_main">
                <Grid item xs={12}>
                    <Box className="padding-24px">
                        <Box className="flex-row-space-between pb-24px">
                            <Typography className="text-500fw-18fs-28lh">
                                {type}
                            </Typography>
                            <Box
                                className="cursor-pointer"
                                onClick={handleClose}
                            >
                                <CloseIcon
                                    color="#98A2B3"
                                    width="12"
                                    height="12"
                                />
                            </Box>
                        </Box>
                        <Box data-testid="partner_dropdown_box">
                            <CustomReactSelect
                                label="Channels"
                                placeholder="Select Channel"
                                selectedOption={selectedChannel}
                                options={uniquePartnerChannelsOptions}
                                handleFilter={handleChannelChange}
                                isOptionsSearchable
                                disable={type === 'Edit'}
                                {...register('channel')}
                            />
                            <span
                                className="text-xs-regular error-500"
                                data-testid="error-message"
                            >
                                {errors.channel?.message}
                            </span>
                        </Box>
                        <Box className="flex-column gap-6 pb-24px w-100 mt-5">
                            <Typography className="metrics-dropdown-title">
                                Action Name
                            </Typography>
                            <input
                                type="text"
                                placeholder="Data Source Name"
                                className="query_name-input-field"
                                {...register('name')}
                                data-testid="ds_name-input-field"
                            />
                            <span
                                className="text-xs-regular error-500"
                                data-testid="error-message"
                            >
                                {errors.name?.message}
                            </span>
                        </Box>
                        <Box className="flex-column gap-6 pb-24px w-100">
                            <Typography className="metrics-dropdown-title">
                                Engagement
                            </Typography>
                            <CustomSwitch
                                checked={isEngaged}
                                onChange={handleIsEngagementChange}
                            />
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} className="flex-1-mt-auto">
                    <Divider className="border-1-gray" />
                    <Box className="flex-row-end-center gap-12 padding-tb-16-rl-24">
                        <CustomButton
                            title="Cancel"
                            handleClick={handleClose}
                            data_test_id="cancel-button"
                        />
                        <CustomButton
                            title="Save"
                            titleColor="#FFFFFF"
                            buttonColor="#AD509E"
                            handleClick={()=> handleSave()}
                            type="submit"
                        />
                    </Box>
                </Grid>
            </Grid>
        </form>
    );
}

export default EditCreateChannelActions;
